// What survives logout, and what does not.
//
// Logout wipes localStorage and sessionStorage so that no token, PII or session
// identifier stays on the device (VAPT Feb 2026, finding 6.2). The keys listed
// here are the only exception: per-device preferences whose key and value carry
// no user id, so they say nothing about who used the browser.
//
// Before adding a key: its name must not contain a user id, and its value must
// not be a token, PII or a session identifier. If it is any of those, leave it
// out and let logout wipe it.
//
// Workspace data is a judgement call. The chart colour slots are kept even
// though their labels are series names (campaign names included): they carry no
// user id and prune themselves. The Customer Journey filters are not kept.
//
// adriel-fe-react runs on the same origin, so its keys are wiped by the same
// call. The React-owned entries below must match the key names in that repo.
//
// The last workspace (lastAccountId) and the last view per workspace
// (LAST_VIEW::accountId_<id>) are NOT kept. The server remembers both per user
// (users.meta, PATCH /user/meta), so after logout the next sign-in reads the
// signed-in user's own values instead of whoever used the device before. The
// device copies still exist during a session: the reload path reads them
// before /auth/user returns.

export const LAST_ACCOUNT_ID_STORAGE_KEY = 'lastAccountId';

export const genLastTabKey = (accountId) =>
    `last_visited_tab_accountId::${accountId}`;

// Key names from before the user id was dropped. They are only read once, by
// migrateLocalStorageKey, and are never preserved across logout.
export const genLegacyLastAccountIdKey = (userId) => `accountId::${userId}`;
export const genLegacyLastTabKey = (userId, accountId) =>
    `last_visited_tab_userId::${userId}_accountId::${accountId}`;

export const LOGOUT_PRESERVED_STORAGE_KEYS = [
    'lang',
    'hasPlaySplash',
    'hasNoPlayDemo',
    // adriel-fe-react: atoms/sidebar.atom.ts (sidebar width in px)
    'dashboardWidth',
    // adriel-fe-react: GraphWidget/atoms/graphColorRegistry.atom.ts. One map
    // per view, chart label -> palette slot, so a label keeps its colour. Keyed
    // by view id only. Entries expire after 15 days and are capped at 100 views.
    'adriel:graph-label-color-slots:v1',
    // adriel-fe-react: Dashboard/MobileGuide/useMobileGuide.ts. True once the
    // "use a desktop" guide was dismissed on this device.
    'isMobileGuideDismissed',
];

// Anchored on purpose: a legacy key such as
// `last_visited_tab_userId::2_accountId::1` carries a user id.
export const LOGOUT_PRESERVED_STORAGE_KEY_PATTERNS = [
    // genLastTabKey
    /^last_visited_tab_accountId::\d+$/,
];

const isPreservedOnLogout = (key) =>
    LOGOUT_PRESERVED_STORAGE_KEYS.includes(key) ||
    LOGOUT_PRESERVED_STORAGE_KEY_PATTERNS.some((pattern) => pattern.test(key));

export const clearStorageOnLogout = () => {
    try {
        const storage = window.localStorage;
        if (storage) {
            // Collect first: removing while iterating shifts the indexes.
            const keys = [];
            for (let i = 0; i < storage.length; i += 1) {
                keys.push(storage.key(i));
            }

            keys.filter((key) => !isPreservedOnLogout(key)).forEach((key) =>
                storage.removeItem(key)
            );
        }

        window.sessionStorage && window.sessionStorage.clear();
    } catch (e) {
        console.error('Failed to clear storage on logout', e);
    }
};

// Moves a value saved under a legacy key to its current key. The legacy key
// belongs to one user, so when it exists it is that user's latest value and
// wins over whatever the shared key holds. Temporary: drop the call sites once
// the legacy keys have aged out.
export const migrateLocalStorageKey = (legacyKey, key) => {
    try {
        const storage = window.localStorage;
        const legacyValue = storage.getItem(legacyKey);

        if (legacyValue === null) return;

        storage.setItem(key, legacyValue);
        storage.removeItem(legacyKey);
    } catch (e) {
        // Storage is blocked: there is nothing to carry over.
    }
};
